/**
 * The value set of `RoleGroupSurface.surface` — an `AppFeatureKey`, or the
 * wildcard that stands for every surface.
 *
 * `RoleGroupSurface` is a STRICT ALLOW-LIST: zero rows grants NOTHING. A group
 * that reaches every surface says so with ONE row holding `*`. That inverts the
 * original design ("zero rows = every surface") on purpose, and the inversion is
 * the whole reason this constant exists as a named export rather than a `"*"`
 * typed at each call site: a bare literal is exactly what the next reader
 * "fixes".
 *
 * Two invisible failures the sentinel removes:
 *
 *  1. Under default-open, whoever changes it to default-deny revokes access in
 *     every organization at once, with no diff to look at.
 *  2. Under default-open, "remove a surface from this group" silently GRANTS
 *     every surface the moment it removes the last row — and `DELETE
 *     /roles/:id/group-grants/:grantId` maps straight onto that shape.
 *
 * Under the sentinel both edits fail in the safe direction: removing rows
 * removes access, and an empty table is the state that grants least.
 *
 * `*` and named surfaces may coexist. The set is the UNION of the rows, so the
 * combination is redundant rather than ambiguous — which is why the wildcard is
 * a ROW and not an `allSurfaces` boolean on `RoleGroup`. A boolean beside the
 * rows would make `allSurfaces = true` WITH named rows genuinely undecidable.
 *
 * The database constrains the SHAPE (`surface = '*' OR surface ~
 * '^[a-z][a-z0-9_]*$'`) and never the membership, because `AppFeatureKey` is a
 * const union precisely so it can grow without a migration. Membership is the
 * write path's job — that is what {@link isPermissionSetSurfaceValue} is for, and
 * skipping it lands the codebase in "TEXT with no validation anywhere", which is
 * the state to avoid and the easy one to reach by accident.
 */
import { z } from "zod";

import {
  APP_FEATURE_KEYS,
  type AppFeatureKey,
  AppFeatureKeySchema
} from "../features/features.constants";

/**
 * Every surface. The same sentinel `ResourcePermission` already uses for a
 * wildcard `resourceId` (`WILDCARD_RESOURCE_ID`), deliberately spelled the same
 * way so the two read alike — but a SEPARATE constant, because a surface and a
 * resource id are different axes and coupling their catalogs would make a change
 * to one silently move the other.
 *
 * No `AppFeatureKey` is spelled `*` (they all match `^[a-z][a-z0-9_]*$`), so the
 * sentinel cannot collide with a real surface.
 */
export const PERMISSION_SET_SURFACE_WILDCARD = "*" as const;

export type PermissionSetSurfaceValue = AppFeatureKey | typeof PERMISSION_SET_SURFACE_WILDCARD;

export const PermissionSetSurfaceValueSchema: z.ZodType<PermissionSetSurfaceValue> = z.union([
  z.literal(PERMISSION_SET_SURFACE_WILDCARD),
  AppFeatureKeySchema
]);

/**
 * Runtime membership check for a `RoleGroupSurface.surface` value.
 *
 * A type guard rather than a cast: the column is TEXT, and a row that predates
 * (or sidesteps) the write path can hold anything the shape CHECK admits. Every
 * path that turns stored text into a surface must go through this.
 */
export const isPermissionSetSurfaceValue = (value: unknown): value is PermissionSetSurfaceValue =>
  value === PERMISSION_SET_SURFACE_WILDCARD ||
  (typeof value === "string" && (APP_FEATURE_KEYS as readonly string[]).includes(value));
